// @ts-check
import * as API from 'aws-amplify/api';

/** @typedef {API['get']|API['post']|API['put']|API['patch']|API['del']} APIService */
/** @typedef {{queryStringParameters?: Record<string,any>, responseType: string }} AmplifyLegacyOptions */
/**
 * @param {Awaited<Awaited<ReturnType<APIService>>['response']>} response
 */
const handleAmplifyResponse = response => {
    return response?.body
        ? response.body.text().then(responseBodyAsText => {
              try {
                  const json = JSON.parse(responseBodyAsText);
                  if ((json?.status || '').toLowerCase() === 'error') {
                      // Response was an error with a status code
                      return Promise.reject(json);
                  }
                  return {
                      code: response.statusCode,
                      message: json,
                      headers: response.headers,
                      status: 'success',
                  };
              } catch (e) {
                  // Response was malformed JSON
                  if (response.statusCode >= 200 && response.statusCode < 300) {
                      // Response had a success code, treat as a success
                      return {
                          code: response.statusCode,
                          message: responseBodyAsText,
                          headers: response.headers,
                          status: 'success',
                      };
                  }
              }
              // Unhandled error
              return responseBodyAsText
                  ? // We had a response body, it's probably a message
                    Promise.reject({
                        code: 500,
                        status: 'error',
                        message: responseBodyAsText,
                        headers: response.headers,
                    })
                  : // We had no response body, return a generic error
                    Promise.reject({
                        code: response.statusCode,
                        status: 'error',
                        message: `common.errors.${response.statusCode}`,
                        headers: response.headers,
                    });
          })
        : Promise.reject({
              code: response.statusCode || 502,
              status: 'error',
              message: `common.errors.${response.statusCode || 502}`,
              headers: response.headers,
          });
};

/**
 * @param {Awaited<ReturnType<handleAmplifyResponse>>} response
 */
const returnMessage = response => {
    if (response.status === 'success') {
        return Promise.resolve(response.message);
    }
    return Promise.reject(new Error(response.message || 'An unknown error occurred'));
};

/**
 * FastAPI returns errors as { detail: "..." } without the status/errorCode
 * fields our Node APIs use. Returns a normalized { status, message } object
 * if the input looks like a FastAPI error, or null otherwise.
 * @param {object|null} json
 * @returns {{ status: string, message: string } | null}
 */
export const parseFastApiError = json => {
    if (json && typeof json.detail === 'string') {
        return { status: 'error', message: json.detail };
    }
    return null;
};

/**
 * On 4xx/5xx, read the response body from the error when possible and rethrow it with { status, errorCode, message }.
 * If the error already has errorCode or we can't get a body, rethrow as-is.
 * @param {{errorCode: string, message: string, response?: {body?: string, data?: string, status?: number, statusCode?: number}, body?: any, data?: any, status?: number, code?: string | number}} error
 * @returns {Promise<void>}
 * @throws {{status: string, errorCode: string, message: string, code?: string | number, response?: {body?: string, data?: string}, body?: any, data?: any}}
 */
export const normalizeRejection = async error => {
    if (error && typeof error.errorCode !== 'undefined') {
        throw error;
    }

    let json = null;
    const body = error?.response?.body ?? error?.body ?? error?.response?.data ?? error?.data;

    try {
        if (typeof body === 'string') {
            json = JSON.parse(body);
        } else if (body && typeof body === 'object' && !(body instanceof Error)) {
            if (typeof body.text === 'function') {
                json = JSON.parse(await body.text());
            } else if (typeof body.json === 'function') {
                json = await body.json();
            } else if (
                body.errorCode !== undefined ||
                (body.status || '').toLowerCase() === 'error'
            ) {
                json = body;
            }
        }
    } catch {
        // ignore parse/read errors
    }

    if (json && ((json.status || '').toLowerCase() === 'error' || json.errorCode !== undefined)) {
        const code = error?.response?.statusCode;
        // Keep the rest of the body: some errors carry details, like the vendor that holds a claimed domain.
        throw {
            ...json,
            status: json.status ?? 'error',
            errorCode: json.errorCode,
            message: json.message,
            ...(code != null && { code }),
        };
    }

    const fastApiError = parseFastApiError(json);
    if (fastApiError) {
        const code = error?.response?.status ?? error?.status ?? error?.code;
        throw { ...fastApiError, ...(code != null && { code }) };
    }

    throw error;
};

/**
 * @template {APIService | API['head']} T
 * @param {Parameters<T>[0]['options'] & AmplifyLegacyOptions} options
 * @return {Parameters<T>[0]['options']}
 */
const formatOptions = options => {
    const { queryStringParameters = {}, responseType, headers, ...rest } = options || {};
    let contentType = headers?.['Content-Type'] || headers?.['content-type'];
    switch (responseType) {
        case 'blob':
        case 'arrayBuffer':
            contentType = 'application/octet-stream';
            break;
        case 'text':
            contentType = 'text/plain';
            break;
        case 'form-data': {
            contentType = 'multipart/form-data';
            break;
        }
        case 'json':
        default:
            contentType = 'application/json';
            break;
    }
    return {
        ...rest,
        headers: {
            ...(headers || {}),
            'Content-Type': contentType,
        },
        queryParams: Object.entries(queryStringParameters).reduce((acc, [key, value]) => {
            if (value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {}),
    };
};

/**
 * @template {APIService | API['head']} T
 * @param {T} service
 * @returns {(apiName: string, path: string, options: Parameters<T>[0]['options'] & AmplifyLegacyOptions) => Promise<Awaited<ReturnType<handleAmplifyResponse>>['message']>}
 */
function amplifyLegacyWrapper(service) {
    return function (apiName, path, options) {
        if (process.env.NODE_ENV === 'development') {
            if (typeof apiName === 'object') {
                // eslint-disable-next-line no-console
                console.warn(
                    `%c Amplify API \n%cYou appear to be using the legacy Amplify API wrapper for an API call, but the first parameter is not a string. You may be wanting to use the current Amplify API implementation. If so, please update your code to use the new Amplify API directly:\n%c import { ${service.name} } from "/b2b/common/helpers/API" `,
                    'color: white; background-color: red; font-weight: bold;',
                    'color: inherit; background-color: inherit;',
                    'color: white; background-color: green; font-weight: bold;'
                );
            }
        }
        const { cancel: _cancel, response } = service({
            options: /** @type {formatOptions<T>} */ (formatOptions)(options),
            apiName,
            path,
        });
        return response.then(handleAmplifyResponse).then(returnMessage).catch(normalizeRejection);
    };
}

/**
 * @template {APIService | API['head']} T
 * @param {T} service
 * @returns {T}
 */
function amplifyLegacyWarning(service) {
    return /** @type {T} */ (
        function (input) {
            if (process.env.NODE_ENV === 'development') {
                if (typeof input === 'string') {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `%c Amplify API \n%cYou appear to be using the current Amplify API implementation, but the first parameter is a string. You may be using the legacy Amplify API implementation. Please update your code to use the new Amplify API signature, or use the legacy Amplify API wrapper from:\n%c import API from "/b2b/common/helpers/API"; \n API.${service.name}(apiName, path, options); `,
                        'color: white; background-color: red; font-weight: bold;',
                        'color: inherit; background-color: inherit;',
                        'color: white; background-color: green; font-weight: bold;'
                    );
                }
            }
            return service(input);
        }
    );
}

export const get = amplifyLegacyWarning(API.get);
export const post = amplifyLegacyWarning(API.post);
export const put = amplifyLegacyWarning(API.put);
export const del = amplifyLegacyWarning(API.del);
export const patch = amplifyLegacyWarning(API.patch);
export const head = amplifyLegacyWarning(API.head);

const defaultExports = {
    get: amplifyLegacyWrapper(API.get),
    post: amplifyLegacyWrapper(API.post),
    put: amplifyLegacyWrapper(API.put),
    del: amplifyLegacyWrapper(API.del),
    patch: amplifyLegacyWrapper(API.patch),
    head: amplifyLegacyWrapper(API.head),
};

export default defaultExports;
